import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { CustomModelForm, type BusinessDescriptionData } from '@/components/forms/CustomModelForm';
import { EmailGateModal, getStoredEmail } from '@/components/modals/EmailGateModal';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

type PageState = 'form' | 'specs' | 'assumptions' | 'logic' | 'generating';

export function CustomModelPage() {
  const [pageState, setPageState] = useState<PageState>('form');
  const [loadingMsg, setLoadingMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Payload progression states
  const [businessData, setBusinessData] = useState<BusinessDescriptionData | null>(null);
  const [specsData, setSpecsData] = useState<any>(null);
  const [assumptionsData, setAssumptionsData] = useState<any[]>([]);
  const [logicData, setLogicData] = useState<any>(null);

  // Email gate
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);

  // Step 1: Form Submit -> Trigger Gate
  const handleFormSubmit = (data: BusinessDescriptionData) => {
    setBusinessData(data);
    const storedEmail = getStoredEmail();
    if (storedEmail) {
      handleStep1GenerateSpecs(data);
    } else {
      setShowEmailGate(true);
    }
  };

  const handleEmailSubmit = async (email: string, name?: string) => {
    if (!businessData) return;
    setIsSubmittingEmail(true);
    setShowEmailGate(false);

    // Background Lead Capture
    try {
      await fetch(`${API_BASE}/auth/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, modelType: 'custom', action: 'generate_specs' }),
      });
    } catch (e) {
      console.warn('Lead capture failed:', e);
    }

    setIsSubmittingEmail(false);
    handleStep1GenerateSpecs(businessData);
  };

  // ----------------------------------------------------------------------
  // PIPELINE EXECUTIONS
  // ----------------------------------------------------------------------

  const handleStep1GenerateSpecs = async (data: BusinessDescriptionData) => {
    setPageState('generating');
    setLoadingMsg('Stage 1/4: Structuring Model Architecture...');
    setErrorMsg(null);

    try {
      // Create comprehensive description from the form
      const fullDesc = `Name: ${data.businessName}
Stage: ${data.businessStage}
Desc: ${data.businessDescription}
Customers: ${data.targetCustomers}
Revenue: ${data.revenueModel}
Costs: ${data.majorCosts}
Metrics: ${data.keyMetrics}
Drivers: ${data.growthDrivers}
Complexity: ${data.complexity}
Pref. Currency: ${data.currency}`;

      const response = await fetch(`${API_BASE}/api/custom/specs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessDescription: fullDesc }),
      });

      const resBase = await response.json();
      if (!resBase.success) throw new Error(resBase.error);

      setSpecsData(resBase.data);
      setPageState('specs');
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to generate specs.');
      setPageState('form');
    }
  };

  const handleStep2GenerateAssumptions = async () => {
    setPageState('generating');
    setLoadingMsg('Stage 2/4: Generating Financial Variables...');
    setErrorMsg(null);

    const fullDesc = Object.entries(businessData || {}).map(([k, v]) => `${k}: ${v}`).join('\n');

    try {
      const response = await fetch(`${API_BASE}/api/custom/assumptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ specs: specsData, businessDescription: fullDesc }),
      });

      const resBase = await response.json();
      if (!resBase.success) throw new Error(resBase.error);

      setAssumptionsData(resBase.data.assumptions || []);
      setPageState('assumptions');
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to generate assumptions.');
      setPageState('specs');
    }
  };

  const handleStep3CompileLogic = async () => {
    setPageState('generating');
    setLoadingMsg('Stage 3/4: Compiling Core Math Engine...');
    setErrorMsg(null);

    const fullDesc = Object.entries(businessData || {}).map(([k, v]) => `${k}: ${v}`).join('\n');

    try {
      const response = await fetch(`${API_BASE}/api/custom/logic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ specs: specsData, assumptions: { assumptions: assumptionsData }, businessDescription: fullDesc }),
      });

      const resBase = await response.json();
      if (!resBase.success) throw new Error(resBase.error);

      setLogicData(resBase.data);
      setPageState('logic');
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to compile core logic engine.');
      setPageState('assumptions');
    }
  };

  const handleStep4DownloadExcel = async () => {
    setPageState('generating');
    setLoadingMsg('Stage 4/4: Building Native Excel File...');
    setErrorMsg(null);

    try {
      const response = await fetch(`${API_BASE}/api/custom/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ specs: specsData, assumptions: { assumptions: assumptionsData }, logic: logicData }),
      });

      if (!response.ok) {
        throw new Error('Failed to download Excel file.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${specsData.modelName.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setPageState('logic'); // return to state
      alert("✅ Custom Model successfully generated and downloaded!");
    } catch (err: any) {
      setErrorMsg(err.message || 'Export failed.');
      setPageState('logic');
    }
  };

  // ----------------------------------------------------------------------
  // UI RENDER HELPERS
  // ----------------------------------------------------------------------

  const updateAssumption = (index: number, newValue: string) => {
    const arr = [...assumptionsData];
    arr[index].value = parseFloat(newValue) || 0;
    setAssumptionsData(arr);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased flex flex-col">
      <Navbar />

      <main className="flex-grow max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        {errorMsg && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            <strong>Error:</strong> {errorMsg}
          </div>
        )}

        <AnimatePresence mode="wait">

          {/* STAGE 0: Initial Form */}
          {pageState === 'form' && (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-10">
                <h1 className="text-4xl font-bold mb-4">AI Custom Model Builder</h1>
                <p className="text-slate-600">The world's first multi-stage agentic LLM architecture building perfectly structured native `.xlsx` files dynamically.</p>
              </div>
              <CustomModelForm onSubmit={handleFormSubmit} isLoading={isSubmittingEmail} />
            </motion.div>
          )}

          {/* LOADING STATE */}
          {pageState === 'generating' && (
            <motion.div key="generating" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-16 shadow border text-center my-20">
              <div className="animate-spin w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold mb-3">{loadingMsg}</h2>
              <p className="text-slate-500">Executing strict Python parameters inside securely sandboxed environments...</p>
            </motion.div>
          )}

          {/* STAGE 1 REVIEW: Specs */}
          {pageState === 'specs' && specsData && (
            <motion.div key="specs" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-xl shadow border p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary-600 border-b pb-4">Step 1: Architecture Reviewed</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Model Name</p>
                  <p className="font-semibold text-sm">{specsData.modelName}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Type</p>
                  <p className="font-semibold text-sm">{specsData.modelType}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Horizon</p>
                  <p className="font-semibold text-sm">{specsData.numberOfPeriods} {specsData.periodFrequency}s</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Base Currency</p>
                  <p className="font-semibold text-sm">{specsData.currency}</p>
                </div>
              </div>
              <div className="mb-8">
                <h3 className="font-bold mb-2">Sheets to Generate:</h3>
                <ul className="list-disc pl-5 space-y-1 text-slate-700">
                  {specsData.outputSheets?.map((s: any, i: number) => <li key={i}>{s.name} <span className="text-xs text-slate-400">({s.type})</span></li>)}
                </ul>
              </div>
              <div className="flex justify-end pt-4 border-t gap-4">
                <button onClick={() => setPageState('form')} className="px-5 py-2 text-slate-500 hover:bg-slate-100 rounded-lg">Go Back</button>
                <button onClick={handleStep2GenerateAssumptions} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium shadow-md">Approve & Generate Variables →</button>
              </div>
            </motion.div>
          )}

          {/* STAGE 2 REVIEW: Assumptions Grid */}
          {pageState === 'assumptions' && (
            <motion.div key="assumptions" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-xl shadow border p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary-600 border-b pb-4">Step 2: Define Variables</h2>
              <p className="text-slate-600 mb-6">Review and edit the custom financial variables dynamically mapped for your prompt.</p>

              <div className="overflow-x-auto mb-8 border rounded-lg">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-sm text-slate-600">
                      <th className="p-3 border-b">Category</th>
                      <th className="p-3 border-b">Variable Label</th>
                      <th className="p-3 border-b">Default Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assumptionsData.map((ast, i) => (
                      <tr key={i} className="border-b hover:bg-slate-50">
                        <td className="p-3 text-sm text-slate-500 font-medium">{ast.category}</td>
                        <td className="p-3">
                          <p className="font-medium text-slate-800 text-sm">{ast.label}</p>
                          <p className="text-xs text-slate-400 max-w-sm truncate">{ast.description}</p>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={ast.value}
                              onChange={(e) => updateAssumption(i, e.target.value)}
                              className="w-32 p-2 border rounded text-right focus:ring-primary-500 focus:border-primary-500 outline-none"
                            />
                            <span className="text-slate-500 text-sm">{ast.unit}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end pt-4 gap-4">
                <button onClick={() => setPageState('specs')} className="px-5 py-2 text-slate-500 hover:bg-slate-100 rounded-lg">← Back to Specs</button>
                <button onClick={handleStep3CompileLogic} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium shadow-md">Compile Math Engine →</button>
              </div>
            </motion.div>
          )}

          {/* STAGE 3 REVIEW: Logic Engine */}
          {pageState === 'logic' && logicData && (
            <motion.div key="logic" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow border p-8">
              <div className="flex items-center justify-between border-b pb-6 mb-8">
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
                    <span className="text-green-500">✓</span> Compilation Success
                  </h2>
                  <p className="text-slate-500 mt-2">The AI strictly locked {logicData.sheets?.reduce((sum: number, sheet: any) => sum + (sheet.calculationFormulas?.length || 0), 0) || 0} independent native math equations across {logicData.sheets?.length || 0} interlinked worksheets.</p>
                </div>
                <button onClick={handleStep4DownloadExcel} className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg flex items-center gap-2 transition-all transform hover:-translate-y-1">
                  📥 Download Interactive Excel
                </button>
              </div>

              <h3 className="font-bold mb-4 text-slate-700 uppercase text-xs tracking-wider">Top Syntax Evaluated:</h3>
              <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                {logicData.sheets?.map((sheet: any, sIdx: number) => (
                  <div key={sIdx}>
                    <h4 className="font-bold text-sm text-primary-600 mb-3 border-b pb-1">{sheet.sheetName}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {sheet.calculationFormulas?.slice(0, 6).map((f: any, i: number) => (
                        <div key={i} className="bg-slate-50 border rounded-lg p-4 font-mono text-sm shadow-sm">
                          <div className="text-primary-600 font-bold mb-2">"{f.output}"</div>
                          <div className="text-slate-600 break-words mb-2">Month 1: <span className="text-indigo-600 font-semibold">{f.formulaPeriod1}</span></div>
                          <div className="text-slate-600 break-words">Ongoing: <span className="text-green-700 font-semibold">{f.formulaOngoing}</span></div>
                        </div>
                      ))}
                      {sheet.calculationFormulas?.length > 6 && (
                        <div className="col-span-1 md:col-span-2 text-center text-slate-400 py-4 italic text-sm">
                          + {sheet.calculationFormulas.length - 6} strictly typed constraints compiled on this sheet...
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </main>

      <Footer />

      <EmailGateModal
        isOpen={showEmailGate}
        onClose={() => setShowEmailGate(false)}
        onSubmit={handleEmailSubmit}
        action="generate"
        modelType="custom"
        isLoading={isSubmittingEmail}
      />
    </div>
  );
}
